import { errorClockMap } from 'constants/errors';
import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { ChatViewMode } from 'enums/ChatViewMode';
import { StorageKey } from 'enums/StorageKey';
import { WidgetFeature } from 'enums/WidgetFeature';
import { DefaultRootState } from 'react-redux';
import { all, put, select, take, takeLatest } from 'redux-saga/effects';
import { services } from 'services/container';
import { setChatViewMode } from 'state/chat/chat.actions';
import { FeatureActionType } from 'state/features/features.actions';
import { selectFeatureEnabled } from 'state/features/features.selectors';
import { flushQueuedGameActions } from 'state/game/game.actions';
import { ParameterActionType } from 'state/parameters/parameters.actions';
import { createReachGoalActionBy } from 'utils/analytics';

import {
    clearHistory,
    flushQueuedHistoryActions,
    flushQueuedScenariosAction,
} from '../history/history.actions';
import { ConnectionActionType, SetAuthedAction, setConnectionError } from './connection.actions';
import { selectIsExecutable } from './connection.selectors';

function* setAuthedSaga() {
    yield takeLatest(ConnectionActionType.SetAuthed, function* (action: SetAuthedAction) {
        const authed = action.payload;

        const state: DefaultRootState = yield select();

        if (authed) {
            yield put(clearHistory());

            if (selectFeatureEnabled(state, WidgetFeature.ErrorScreen)) {
                if (state.connection.error) yield put(setConnectionError(null));
            }

            yield put(flushQueuedHistoryActions());
            yield put(flushQueuedGameActions());

            const shouldNavigateToGallery = localStorage.getItem(StorageKey.MediaDownloaded);
            if (shouldNavigateToGallery) {
                localStorage.removeItem(StorageKey.MediaDownloaded);

                yield put(setChatViewMode(ChatViewMode.Gallery));
            }
        }
    });
}

function* scanAuthZoneSaga() {
    yield takeLatest(
        [
            ConnectionActionType.SetAuthed,
            ParameterActionType.Init,
            FeatureActionType.SetFeaturesActive,
        ],
        function* () {
            const state = yield select();
            const isExecutable = selectIsExecutable(state);

            if (isExecutable) {
                yield put(flushQueuedScenariosAction());
            }
        },
    );
}

function* connectionErrorSaga() {
    let lastError: { code: number; subCode?: number } = null;
    while (true) {
        yield take(ConnectionActionType.SetConnectionError);

        const state: DefaultRootState = yield select();

        if (!selectFeatureEnabled(state, WidgetFeature.ErrorScreen)) continue;

        const { error } = state.connection;

        if (!error) {
            lastError = null;
            continue;
        }

        const { code, subCode } = error;

        if (code === lastError?.code && subCode === lastError?.subCode) continue;

        lastError = error;

        const errorDescriptor = errorClockMap[code];

        if (errorDescriptor) {
            const subErrorDescriptor = errorDescriptor.subErrors?.[subCode];
            const errorMsg = subErrorDescriptor?.eventName || errorDescriptor?.eventName;

            const eventName = 'МиЧБ. Ошибка.' + (errorMsg ? ' ' + errorMsg : '');
            services.analytics.sendScreenViewEvent(eventName, 'Экран ошибки');

            services.analytics.send(
                createReachGoalActionBy(AnalyticsTarget.SHOW_CHAT_STATUS, errorMsg),
            );
        }
    }
}

export function* connectionSaga() {
    yield all([setAuthedSaga(), scanAuthZoneSaga(), connectionErrorSaga()]);
}
